import { useState } from 'react';

import PropTypes from 'prop-types';

import { useIntl } from '@zenbusiness/application-commons-intl';
import Chip from '@zenbusiness/zui/Chip';
import Snackbar from '@zenbusiness/zui/Snackbar';

import useEmailVerified from '#client/hooks/useEmailVerified';
import { useEnterprise } from '#client/hooks/useEnterprise';
import useSendVerificationEmail from '#client/hooks/useSendVerificationEmail';

import * as Styled from './EmailVerificationStatus.style';

const MESSAGE_KEY = 'dashboard.accountSettings.email';
const MESSAGE_IDS = {
  verified: `${MESSAGE_KEY}.verified`,
  unverified: `${MESSAGE_KEY}.unverified`,
  resendVerification: `${MESSAGE_KEY}.resendVerification`,
  resendSuccess: `${MESSAGE_KEY}.resendSuccess`,
  resendRateLimited: `${MESSAGE_KEY}.resendRateLimited`,
  resendAlreadyVerified: `${MESSAGE_KEY}.resendAlreadyVerified`,
  resendFederated: `${MESSAGE_KEY}.resendFederated`,
  resendFailed: `${MESSAGE_KEY}.resendFailed`,
};

const TOAST_DURATION_MS = 6000;
const TERMINAL_ERROR_CODES = new Set(['alreadyVerified', 'federatedIdentity']);

function toastMessageId(result) {
  if (result?.sent) {
    return MESSAGE_IDS.resendSuccess;
  }
  if (result?.errorCode === 'rateLimited') {
    return MESSAGE_IDS.resendRateLimited;
  }
  if (result?.errorCode === 'alreadyVerified') {
    return MESSAGE_IDS.resendAlreadyVerified;
  }
  if (result?.errorCode === 'federatedIdentity') {
    return MESSAGE_IDS.resendFederated;
  }
  return MESSAGE_IDS.resendFailed;
}

function EmailVerificationStatus({ email, isSocialLogin = false }) {
  const { formatMessage: fm } = useIntl();
  const emailVerified = useEmailVerified();
  const { enterprise } = useEnterprise();
  const { sendVerificationEmail, loading } = useSendVerificationEmail();
  const [toastId, setToastId] = useState(null);
  const [terminalError, setTerminalError] = useState(null);

  const showStatus = emailVerified === true || emailVerified === false;
  const showResend = emailVerified === false && !isSocialLogin && !enterprise && !terminalError;

  const handleResend = async () => {
    const result = await sendVerificationEmail();
    if (TERMINAL_ERROR_CODES.has(result?.errorCode)) {
      setTerminalError(result.errorCode);
    }
    setToastId(toastMessageId(result));
  };

  return (
    <>
      <Styled.EmailRow>
        <Styled.EmailValue component="p" size="md" color="primary">
          {email}
        </Styled.EmailValue>
        {showStatus ? (
          <Chip
            color={emailVerified ? 'success' : 'warning'}
            label={fm({
              id: emailVerified ? MESSAGE_IDS.verified : MESSAGE_IDS.unverified,
            })}
            size="small"
          />
        ) : null}
      </Styled.EmailRow>
      {showResend ? (
        <Styled.ResendLink
          clickstreamProps={{
            appSection: 'account',
            elementName: 'resend_verification_email',
          }}
          color="info"
          disabled={loading}
          onClick={handleResend}
          size="md"
          variant="text"
        >
          {fm({ id: MESSAGE_IDS.resendVerification })}
        </Styled.ResendLink>
      ) : null}
      {toastId ? (
        <Snackbar
          autoHideDuration={TOAST_DURATION_MS}
          dismissable
          onClose={() => setToastId(null)}
          open
          title={fm({ id: toastId })}
          variant="action"
        />
      ) : null}
    </>
  );
}

EmailVerificationStatus.propTypes = {
  email: PropTypes.string,
  isSocialLogin: PropTypes.bool,
};

export default EmailVerificationStatus;
